






































































import {
  computed,
  defineComponent,
  nextTick,
  PropType,
  ref,
  toRefs,
  watch,
} from '@nuxtjs/composition-api';
import { BaseButton, BaseInput, BaseList, BaseListItem, UCIcon } from '@shared/ui';
import debounce from 'lodash/debounce';
import sanitizeHtml from 'sanitize-html';
import clickOutside from 'vue-click-outside';

const nextEnabledResultIndex = (results: SearchResult[], startIndex: number) =>
  results.findIndex((result, index) => index >= startIndex && !result.disabled);

const prevEnabledResultIndex = (results: SearchResult[], startIndex: number) =>
  // @ts-ignore findLastIndex not recognized by this version of TS
  results.findLastIndex((result, index) => index <= startIndex && !result.disabled);

export interface SearchResult {
  label: string;
  htmlLabel: string;
  [key: string]: unknown;
  disabled?: boolean;
}

export default defineComponent({
  name: 'SearchList',
  directives: {
    clickOutside,
  },
  components: {
    BaseInput,
    BaseList,
    BaseListItem,
    BaseButton,
    UCIcon,
  },
  props: {
    preloadedSearchInput: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabledResultText: {
      type: String,
      default: 'Hello',
    },
    required: {
      type: Boolean,
      default: false,
    },
    resultsLoading: {
      type: Boolean,
      default: false,
    },
    resultsErrorMessage: {
      type: String,
      default: '',
    },
    results: {
      type: Array as PropType<SearchResult[]>,
      default: () => [],
    },
    isPostcode: {
      type: Boolean,
      default: false,
    },
    clearInputOnSelect: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const { preloadedSearchInput, results, resultsErrorMessage } = toRefs(props);

    const searchInput = ref(props.preloadedSearchInput);
    const showResults = ref(false);
    const highlightedIndex = ref(-1);
    const componentRef = ref<HTMLDivElement | null>(null);

    const onKeyEnter = () => {
      const highlightedResult = results.value[highlightedIndex.value];
      if (!highlightedResult) {
        return;
      }
      selectSearchResult(highlightedResult);
    };
    const onKeyEsc = () => {
      searchInput.value = '';
      closeResults();
    };
    const onKeyDownArrow = async () => {
      if (!hasResults.value || highlightedIndex.value === results.value.length - 1) {
        return;
      }
      highlightedIndex.value = nextEnabledResultIndex(results.value, highlightedIndex.value + 1);
      await scrollResultIntoView();
    };
    const onKeyUpArrow = async () => {
      if (!hasResults.value || highlightedIndex.value === 0) {
        return;
      }
      highlightedIndex.value = prevEnabledResultIndex(results.value, highlightedIndex.value - 1);
      await scrollResultIntoView();
    };

    const scrollResultIntoView = async () => {
      await nextTick();
      componentRef.value
        ?.querySelector('.search-list__results-list-item--highlighted')
        // @ts-ignore container is a valid option
        ?.scrollIntoView({ block: 'end', container: 'nearest' });
    };

    watch(
      preloadedSearchInput,
      () => {
        searchInput.value = props.preloadedSearchInput;
      },
      { immediate: true },
    );

    watch(searchInput, () => {
      highlightedIndex.value = -1;
    });

    const hasResults = computed(() => {
      return results.value && Array.isArray(results.value) && results.value.length > 0;
    });

    const errorMessage = computed(() => {
      if (!searchInput.value) return '';
      return resultsErrorMessage.value;
    });

    const expandResults = () => {
      showResults.value = true;
    };

    const closeResults = () => {
      if (props.clearInputOnSelect) {
        searchInput.value = '';
      }
      showResults.value = false;
    };

    const selectSearchResult = (searchResult: SearchResult) => {
      searchInput.value = searchResult.label;
      emit('select-search-result', searchResult);
      closeResults();
    };

    const onFocus = () => {
      if (searchInput.value && results.value.length !== 0) expandResults();
    };

    const debouncedSearch = debounce((searchValue: string) => {
      if (searchValue) {
        emit('search', searchValue);

        if (!props.isPostcode || (props.isPostcode && searchValue.length === 4)) {
          expandResults();
        }
      } else {
        closeResults();
      }
    }, 200);

    // Enforce numeric input only
    const onInput = () => {
      if (props.isPostcode) {
        searchInput.value = searchInput.value.replace(/\D/g, ''); // Prevent non-numeric characters
      }
      debouncedSearch(searchInput.value); // Trigger the debounced search with numeric value
    };

    const sanitizeLabel = (label: string): string =>
      sanitizeHtml(label, {
        allowedTags: ['em'],
      });

    const isHighlighted = (itemIndex: number) => itemIndex === highlightedIndex.value;

    watch(searchInput, (newVal) => {
      if (!newVal) {
        emit('clear');
      }
    });

    return {
      componentRef,
      searchInput,
      showResults,
      hasResults,
      errorMessage,
      sanitizeLabel,
      debouncedSearch,
      selectSearchResult,
      onFocus,
      closeResults,
      expandResults,
      isHighlighted,
      onInput,
      onKeyEnter,
      onKeyEsc,
      onKeyDownArrow,
      onKeyUpArrow,
    };
  },
});
